import { detectCountry, getCachedCountry, persistCountry, clearCachedCountry } from './pricing';

/** Always shown as the 8th option; never part of the 7-flag subset. */
export const AUTO_ARABIC = {
  id: 'auto-arabic',
  name: 'Auto Arabic Dialect',
  flag: '🇦🇪',
  native: 'العربية',
};

/** Full Arabic dialect catalog (subset of 7 is chosen per geo). */
export const ALL_ARABIC_DIALECTS = [
  { id: 'moroccan', name: 'Moroccan', flag: '🇲🇦', native: 'الدارجة' },
  { id: 'egyptian', name: 'Egyptian', flag: '🇪🇬', native: 'مصري' },
  { id: 'saudi', name: 'Saudi', flag: '🇸🇦', native: 'سعودي' },
  { id: 'lebanese', name: 'Lebanese', flag: '🇱🇧', native: 'لبناني' },
  { id: 'algerian', name: 'Algerian', flag: '🇩🇿', native: 'جزائري' },
  { id: 'tunisian', name: 'Tunisian', flag: '🇹🇳', native: 'تونسي' },
  { id: 'jordanian', name: 'Jordanian', flag: '🇯🇴', native: 'الأردني' },
  { id: 'iraqi', name: 'Iraqi', flag: '🇮🇶', native: 'عراقي' },
  { id: 'syrian', name: 'Syrian', flag: '🇸🇾', native: 'سوري' },
  { id: 'palestinian', name: 'Palestinian', flag: '🇵🇸', native: 'فلسطيني' },
  { id: 'kuwaiti', name: 'Kuwaiti', flag: '🇰🇼', native: 'كويتي' },
  { id: 'qatari', name: 'Qatari', flag: '🇶🇦', native: 'قطري' },
  { id: 'bahraini', name: 'Bahraini', flag: '🇧🇭', native: 'بحريني' },
  { id: 'omani', name: 'Omani', flag: '🇴🇲', native: 'عماني' },
  { id: 'yemeni', name: 'Yemeni', flag: '🇾🇪', native: 'يمني' },
  { id: 'libyan', name: 'Libyan', flag: '🇱🇾', native: 'ليبي' },
  { id: 'sudanese', name: 'Sudanese', flag: '🇸🇩', native: 'سوداني' },
];

const DIALECT_BY_ID = Object.fromEntries(
  [...ALL_ARABIC_DIALECTS, AUTO_ARABIC].map((d) => [d.id, d])
);

/** Legacy alias used in older jobs / UI. */
DIALECT_BY_ID.emirati = AUTO_ARABIC;

/** ISO country code → primary dialect id. */
const COUNTRY_TO_DIALECT = {
  MA: 'moroccan',
  DZ: 'algerian',
  TN: 'tunisian',
  LY: 'libyan',
  MR: 'moroccan',
  EG: 'egyptian',
  SD: 'sudanese',
  SA: 'saudi',
  AE: 'saudi',
  KW: 'kuwaiti',
  QA: 'qatari',
  BH: 'bahraini',
  OM: 'omani',
  YE: 'yemeni',
  LB: 'lebanese',
  SY: 'syrian',
  JO: 'jordanian',
  PS: 'palestinian',
  IQ: 'iraqi',
};

/** Landing-page market codes → ISO (soft fallback). */
const GEO_MARKET_TO_COUNTRY = {
  ma: 'MA',
  eg: 'EG',
  ae: 'AE',
  sa: 'SA',
  lb: 'LB',
  tn: 'TN',
};

/** Neighbors used to fill the remaining 6 slots after the primary. */
const REGIONAL_NEIGHBORS = {
  moroccan: ['algerian', 'tunisian', 'libyan', 'egyptian', 'saudi', 'lebanese'],
  algerian: ['moroccan', 'tunisian', 'libyan', 'egyptian', 'saudi', 'lebanese'],
  tunisian: ['algerian', 'libyan', 'moroccan', 'egyptian', 'saudi', 'lebanese'],
  libyan: ['tunisian', 'egyptian', 'algerian', 'sudanese', 'moroccan', 'saudi'],
  egyptian: ['sudanese', 'saudi', 'lebanese', 'jordanian', 'libyan', 'moroccan'],
  sudanese: ['egyptian', 'saudi', 'yemeni', 'libyan', 'lebanese', 'moroccan'],
  saudi: ['kuwaiti', 'qatari', 'bahraini', 'omani', 'yemeni', 'egyptian'],
  kuwaiti: ['saudi', 'qatari', 'bahraini', 'iraqi', 'omani', 'egyptian'],
  qatari: ['saudi', 'kuwaiti', 'bahraini', 'omani', 'egyptian', 'iraqi'],
  bahraini: ['saudi', 'kuwaiti', 'qatari', 'omani', 'egyptian', 'iraqi'],
  omani: ['saudi', 'yemeni', 'qatari', 'kuwaiti', 'bahraini', 'egyptian'],
  yemeni: ['saudi', 'omani', 'egyptian', 'sudanese', 'kuwaiti', 'lebanese'],
  lebanese: ['syrian', 'palestinian', 'jordanian', 'egyptian', 'saudi', 'iraqi'],
  syrian: ['lebanese', 'jordanian', 'palestinian', 'iraqi', 'egyptian', 'saudi'],
  jordanian: ['palestinian', 'syrian', 'lebanese', 'egyptian', 'saudi', 'iraqi'],
  palestinian: ['jordanian', 'lebanese', 'syrian', 'egyptian', 'saudi', 'iraqi'],
  iraqi: ['kuwaiti', 'saudi', 'syrian', 'jordanian', 'egyptian', 'lebanese'],
};

/** Global popularity fill order when neighbors are exhausted. */
const GLOBAL_POPULARITY = [
  'egyptian',
  'moroccan',
  'saudi',
  'lebanese',
  'algerian',
  'tunisian',
  'iraqi',
  'jordanian',
  'syrian',
  'palestinian',
  'kuwaiti',
  'sudanese',
  'libyan',
  'yemeni',
  'qatari',
  'omani',
  'bahraini',
];

const DEFAULT_PRIMARY = 'moroccan';
const SUBSET_SIZE = 7;

const GEO_MARKET_CACHE_KEY = 'arabcap_geo_market';

/** Landing-page style market codes + `world`. */
const SUPPORTED_GEO_MARKETS = new Set(['ma', 'eg', 'ae', 'sa', 'lb', 'tn', 'world']);

function normalizeCountry(code) {
  if (!code || typeof code !== 'string') return null;
  const upper = code.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(upper) ? upper : null;
}

function countryFromGeoMarket() {
  try {
    const market = (localStorage.getItem(GEO_MARKET_CACHE_KEY) || '').toLowerCase();
    return GEO_MARKET_TO_COUNTRY[market] || null;
  } catch {
    return null;
  }
}

function persistGeoMarket(market) {
  try {
    localStorage.setItem(GEO_MARKET_CACHE_KEY, market);
  } catch {
    // ignore
  }
}

/**
 * Parse ?geo= override.
 * Accepts landing markets (`ma`, `eg`, `world`) or ISO country codes (`MA`, `JO`, `KW`).
 * Returns `{ country, market }` where country may be null for `world`.
 */
export function parseGeoOverride(value) {
  if (!value || typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();
  if (SUPPORTED_GEO_MARKETS.has(lower)) {
    if (lower === 'world') return { country: null, market: 'world' };
    return { country: GEO_MARKET_TO_COUNTRY[lower], market: lower };
  }

  const country = normalizeCountry(raw);
  if (!country) return null;

  // Any valid ISO-2 works (dialect map falls back to moroccan for unknown countries)
  const market =
    Object.entries(GEO_MARKET_TO_COUNTRY).find(([, iso]) => iso === country)?.[0] || null;
  return { country, market };
}

/**
 * Read ?geo= from the current URL (sync). Does not persist.
 */
export function getGeoOverrideFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return parseGeoOverride(params.get('geo'));
  } catch {
    return null;
  }
}

/**
 * Apply ?geo= override: persist to storage and return ISO country (or null for world).
 * Examples: ?geo=ma | ?geo=MA | ?geo=jo | ?geo=world
 */
export function applyGeoOverrideFromUrl() {
  const override = getGeoOverrideFromUrl();
  if (!override) return undefined; // no param — caller continues normal resolve

  if (override.market === 'world' || override.country === null) {
    clearCachedCountry();
    persistGeoMarket('world');
    return null;
  }

  persistCountry(override.country);
  if (override.market) persistGeoMarket(override.market);
  return override.country;
}

export function getDialectById(id) {
  if (!id) return null;
  return DIALECT_BY_ID[id] || null;
}

export function getPrimaryDialectId(countryCode) {
  const country = normalizeCountry(countryCode);
  if (!country) return DEFAULT_PRIMARY;
  return COUNTRY_TO_DIALECT[country] || DEFAULT_PRIMARY;
}

/**
 * Build exactly 7 dialect options: local dialect first, then regional neighbors,
 * then global popularity. Auto Arabic is never included.
 */
export function getDialectSubset(countryCode) {
  const primaryId = getPrimaryDialectId(countryCode);
  const seen = new Set([AUTO_ARABIC.id, 'emirati']);
  const ids = [];

  const push = (id) => {
    if (!id || seen.has(id) || !DIALECT_BY_ID[id] || id === AUTO_ARABIC.id) return;
    seen.add(id);
    ids.push(id);
  };

  push(primaryId);

  for (const id of REGIONAL_NEIGHBORS[primaryId] || []) {
    if (ids.length >= SUBSET_SIZE) break;
    push(id);
  }

  for (const id of GLOBAL_POPULARITY) {
    if (ids.length >= SUBSET_SIZE) break;
    push(id);
  }

  return ids.slice(0, SUBSET_SIZE).map((id) => DIALECT_BY_ID[id]);
}

/**
 * Resolve ISO country for dialect selection.
 * Priority: ?geo= URL override → cached country / market → IP detect → market fallback.
 */
export async function resolveGeoCountry() {
  const fromUrl = applyGeoOverrideFromUrl();
  // undefined = no ?geo= param; null = explicit world
  if (fromUrl !== undefined) return fromUrl;

  const cached = getCachedCountry() || countryFromGeoMarket();
  if (cached) return cached;

  // No cache: detect fresh (with timeouts / timezone fallbacks inside detectCountry)
  const detected = await detectCountry({ force: true });
  if (detected) return detected;

  return countryFromGeoMarket();
}
